/**
 * Notices when the GraphQL API has gone away (the box is down or being
 * deployed) so the customer UI can show a maintenance screen instead of
 * broken pages. Framework-free: the Apollo client feeds it, a React hook
 * (shared/ui/maintenance) reads it and runs the re-checks.
 *
 * Only transport failures count: a fetch that throws (network failure, DNS,
 * CORS-less proxy error page) or a 502/503/504 from the proxy. Anything the
 * API itself answers - including GraphQL errors, auth failures, 4xx and 500 -
 * means it is up.
 *
 * A failed request only makes the API "suspect". Only the re-check probe
 * (shared/ui/maintenance) can confirm it "down", and it checks the site's own
 * origin first: a visitor whose own connection has dropped (a lift, a tunnel,
 * a packed course - the browser often still says it is online) gets "offline",
 * which clears the suspicion instead. So a burst of failures on one blip, or a
 * page that keeps polling through a dead signal, never shows the screen.
 */

export type OutageState = 'up' | 'suspect' | 'down';

/** How long after a first failure the probe re-checks (and can confirm an outage). */
export const OUTAGE_CONFIRM_MS = 5000;
/** How often the API is re-checked while it is down. */
export const OUTAGE_RECHECK_MS = 30000;
/** How long a probe waits: a stopped server can leave a request hanging for minutes. */
export const PROBE_TIMEOUT_MS = 8000;

/** A probe's verdict. `offline` = the visitor's own connection is down, which says nothing about the API. */
export type ProbeResult = 'up' | 'down' | 'offline';

export interface OutageMonitor {
  state(): OutageState;
  /** A real request failed: the API becomes suspect (never down on its own). */
  reportFailure(): void;
  /** The probe could not reach the API but could reach the site: suspect becomes down. */
  confirmOutage(): void;
  /** The probe found the visitor offline: that says nothing about the API, so a suspicion is dropped. */
  clearSuspicion(): void;
  reportSuccess(): void;
  subscribe(listener: () => void): () => void;
}

export function createOutageMonitor(): OutageMonitor {
  let current: OutageState = 'up';
  const listeners = new Set<() => void>();
  const set = (next: OutageState) => {
    if (next === current) return;
    current = next;
    listeners.forEach((l) => l());
  };
  return {
    state: () => current,
    reportFailure() {
      if (current === 'up') set('suspect');
    },
    confirmOutage() {
      if (current === 'suspect') set('down');
    },
    clearSuspicion() {
      if (current === 'suspect') set('up');
    },
    reportSuccess: () => set('up'),
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
  };
}

/** The one monitor every customer app uses: its Apollo client reports into it. */
export const apiOutage = createOutageMonitor();

/** A status only a proxy or load balancer in front of a missing API returns. */
export const isOutageStatus = (status: number) => status === 502 || status === 503 || status === 504;

/** A thrown fetch error that means the API could not be reached (not a cancelled request). */
export const isOutageError = (err: unknown) => (err as { name?: unknown } | null)?.name !== 'AbortError';

/** The browser says it has no network at all. */
export const isClientOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

/** A fetch that reports every request's outcome to the monitor, for Apollo's HttpLink. */
export function monitoredFetch(monitor: OutageMonitor): typeof fetch {
  return async (input, init) => {
    let res: Response;
    try {
      res = await fetch(input, init);
    } catch (err) {
      if (isOutageError(err) && !isClientOffline()) monitor.reportFailure();
      throw err;
    }
    if (isOutageStatus(res.status)) monitor.reportFailure();
    else monitor.reportSuccess();
    return res;
  };
}

/** An abort signal that fires after `ms` (AbortSignal.timeout where the browser has it). */
function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === 'function') return AbortSignal.timeout(ms);
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

/**
 * Asks the API the cheapest question it can answer, `{ __typename }`: `up` when
 * it answered at all (whatever the status, bar 502/503/504). When it can't be
 * reached, `reachUrl` on the site's own origin (CloudFront, separate from the
 * API) tells the two apart: reachable means the API is `down`, unreachable
 * means the visitor is `offline`. Each request gives up after `timeoutMs`.
 */
export async function probeApi(
  url: string,
  { reachUrl = '/maintenance.json', timeoutMs = PROBE_TIMEOUT_MS }: { reachUrl?: string; timeoutMs?: number } = {},
): Promise<ProbeResult> {
  if (isClientOffline()) return 'offline';
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ query: '{ __typename }' }),
      credentials: 'omit',
      cache: 'no-store',
      signal: timeoutSignal(timeoutMs),
    });
    return isOutageStatus(res.status) ? 'down' : 'up';
  } catch {
    try {
      await fetch(`${reachUrl}?reach=${Date.now()}`, { cache: 'no-store', signal: timeoutSignal(timeoutMs) });
      return 'down';
    } catch {
      return 'offline';
    }
  }
}
